import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { formatApiErrorDetail } from "../lib/api";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ShieldCheck, TrendingUp, FileText, Landmark } from "lucide-react";

export default function LoginPage() {
  const { user, login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "forgot") {
        await api.post("/auth/forgot-password", { email });
        setResetSent(true);
        setLoading(false);
        return;
      }
      if (mode === "login") await login(email, password);
      else await register(email, password, name || "Business Owner");
      navigate("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const fillDemo = () => {
    setMode("login");
    setEmail("garas49@hotmail.com");
    setPassword("Ledger@2026");
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between bg-primary p-12 relative overflow-hidden">
        <div className="relative z-10">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-primary-foreground/10 grid place-items-center">
              <span className="text-primary-foreground font-heading font-extrabold text-2xl">K</span>
            </div>
            <span className="text-primary-foreground font-heading font-bold text-xl">KangarooLedger</span>
          </div>
          <h1 className="mt-16 text-primary-foreground font-heading font-bold text-4xl xl:text-5xl leading-tight tracking-tight">
            Bookkeeping & GST,<br />built for Australian business.
          </h1>
          <p className="mt-5 text-primary-foreground/60 text-base max-w-md leading-relaxed">
            Track income and expenses, import bank feeds, calculate GST the ATO way, and generate your quarterly BAS in minutes.
          </p>
          <div className="mt-10 space-y-4 max-w-sm">
            {[
              { icon: TrendingUp, t: "Automatic 1/11th GST on every transaction" },
              { icon: Landmark, t: "Import CBA, ANZ, NAB & Westpac CSV feeds" },
              { icon: FileText, t: "ATO-formatted BAS with 1A / 1B / 8A" },
            ].map((f, i) => (
              <div key={i} className="flex items-center gap-3 text-primary-foreground/80 text-sm">
                <f.icon className="w-5 h-5 shrink-0" />
                {f.t}
              </div>
            ))}
          </div>
        </div>
        <div className="relative z-10 text-primary-foreground/40 text-xs font-mono">
          Not affiliated with the ATO · For business record-keeping
        </div>
        <div className="absolute -bottom-24 -right-24 w-96 h-96 rounded-full bg-primary-foreground/5" />
        <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-primary-foreground/5" />
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-12 bg-background">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <div className="w-9 h-9 rounded-xl bg-primary grid place-items-center">
              <span className="text-primary-foreground font-heading font-extrabold">K</span>
            </div>
            <span className="font-heading font-bold text-lg">KangarooLedger</span>
          </div>

          <h2 className="font-heading text-2xl font-bold tracking-tight">
            {mode === "login" ? "Welcome back" : mode === "register" ? "Create your account" : "Reset your password"}
          </h2>
          <p className="text-muted-foreground text-sm mt-1">
            {mode === "login" ? "Sign in to manage your books." : mode === "register" ? "Start tracking GST and BAS today." : "Enter your email and we'll send a reset link."}
          </p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            {mode === "register" && (
              <div>
                <Label htmlFor="name">Business / your name</Label>
                <Input id="name" data-testid="name-input" value={name}
                  onChange={(e) => setName(e.target.value)} className="mt-1.5" placeholder="Jane's Trades" />
              </div>
            )}
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required data-testid="email-input" value={email}
                onChange={(e) => setEmail(e.target.value)} className="mt-1.5" placeholder="you@business.com.au" />
            </div>
            {mode !== "forgot" && (
              <div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">Password</Label>
                  {mode === "login" && (
                    <button type="button" data-testid="forgot-password-link"
                      onClick={() => { setMode("forgot"); setError(""); setResetSent(false); }}
                      className="text-xs text-primary hover:underline">Forgot password?</button>
                  )}
                </div>
                <Input id="password" type="password" required data-testid="password-input" value={password}
                  onChange={(e) => setPassword(e.target.value)} className="mt-1.5" placeholder="••••••••" />
              </div>
            )}

            {mode === "forgot" && resetSent && (
              <div data-testid="reset-sent-msg" className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
                If an account exists for that email, a reset link is on its way. Check your inbox.
              </div>
            )}

            {error && (
              <div data-testid="auth-error" className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-lg px-3 py-2">
                {error}
              </div>
            )}

            <Button type="submit" disabled={loading} data-testid="submit-btn"
              className="w-full h-11 rounded-lg font-medium active:scale-[0.98] transition-transform">
              {loading ? "Please wait…" : mode === "login" ? "Sign in" : mode === "register" ? "Create account" : "Send reset link"}
            </Button>
          </form>

          {mode === "login" && (
            <button onClick={fillDemo} data-testid="demo-fill-btn"
              className="mt-3 w-full h-11 rounded-lg border border-border bg-secondary text-secondary-foreground text-sm font-medium flex items-center justify-center gap-2 hover:bg-secondary/70 transition-colors">
              <ShieldCheck className="w-4 h-4" /> Use demo account
            </button>
          )}

          <p className="mt-6 text-center text-sm text-muted-foreground">
            {mode === "forgot" ? (
              <button type="button" data-testid="back-to-login-btn"
                onClick={() => { setMode("login"); setError(""); setResetSent(false); }}
                className="text-primary font-medium hover:underline">Back to sign in</button>
            ) : (
              <>
                {mode === "login" ? "New here?" : "Already have an account?"}{" "}
                <button
                  type="button"
                  data-testid="toggle-mode-btn"
                  onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}
                  className="text-primary font-medium hover:underline"
                >
                  {mode === "login" ? "Create an account" : "Sign in"}
                </button>
              </>
            )}
          </p>
        </div>
      </div>
    </div>
  );
}
